import React, { useState, useEffect } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';

export default function ProductCategoryPage() {
    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        id: "",
        name: "",
        image: null
    });

    const AuthToken = localStorage.getItem("authToken");

    // ================= FETCH =================
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;
            const res = await axios.get(
                process.env.REACT_APP_API_URL + "/product-categories"
            );
            setCategories(res.data.product_categories || []);
        } catch (err) {
            console.error("Fetch Error:", err);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to load categories',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setLoading(false);
        }
    };

    // ================= ADD =================
    const handleAdd = () => {
        setFormData({ id: "", name: "", image: null });
        setPreview(null);
        setIsEdit(false);
        setShowModal(true);
    };

    // ================= EDIT =================
    const handleEdit = (item) => {
        setFormData({
            id: item.id,
            name: item.name,
            image: null
        });
        setPreview(item.img ? process.env.REACT_APP_IMG_URL + item.img : null);
        setIsEdit(true);
        setShowModal(true);
    };

    // ================= DELETE =================
    const handleDelete = async (item) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You are about to delete category "${item.name}"`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/product-categories-delete/${item.id}`,
                    { headers: { Authorization: `Bearer ${AuthToken}` } }
                );
                
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Category deleted successfully',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
                
                fetchCategories();
            } catch (err) {
                console.error("Delete Error:", err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: err.response?.data?.message || 'Failed to delete category',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        }
    };

    // ================= FILE CHANGE =================
    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Invalid File Type',
                    text: 'Please select a valid image file (JPEG, PNG, GIF, WEBP)',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
                return;
            }
            
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire({
                    icon: 'warning',
                    title: 'File Too Large',
                    text: 'Please select an image less than 5MB',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
                return;
            }
            
            setFormData({ ...formData, image: file });
            setPreview(URL.createObjectURL(file));
        }
    };

    // ================= SUBMIT =================
    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please enter category name',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return;
        }

        setSubmitting(true);
        
        try {
            const form = new FormData();
            form.append("name", formData.name);

            if (formData.image) {
                form.append("img", formData.image);
            }

            let response;
            if (isEdit) {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/product-categories-update/${formData.id}`,
                    form,
                    { headers: { Authorization: `Bearer ${AuthToken}` } }
                );
                
                Swal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: 'Category updated successfully',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/product-categories`,
                    form,
                    { headers: { Authorization: `Bearer ${AuthToken}` } }
                );
                
                Swal.fire({
                    icon: 'success',
                    title: 'Saved!',
                    text: 'Category added successfully',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }

            setShowModal(false);
            setFormData({ id: "", name: "", image: null });
            setPreview(null);
            fetchCategories();
            
        } catch (err) {
            console.error("Submit Error:", err);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: err.response?.data?.message || 'Failed to save category',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">Loading categories...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div className="container-fluid mt-4">
                <div className="mc-card">
                    <div className="mc-breadcrumb">
                        <h3 className="mc-breadcrumb-title">Product Categories</h3>
                        <ul className="mc-breadcrumb-list">
                            <li className="mc-breadcrumb-item">
                                <a href="#" className="mc-breadcrumb-link">Home</a>
                            </li>
                            <li className="mc-breadcrumb-item">
                                <a href="#" className="mc-breadcrumb-link">Products</a>
                            </li>
                            <li className="mc-breadcrumb-item">Categories</li>
                        </ul>
                    </div>
                </div>

                <div className="mc-card">
                    <div className="mc-card-header d-flex justify-content-between align-items-center">
                        <h5 className="mc-card-title mb-0">All Categories</h5>
                        <Button 
                            onClick={handleAdd} 
                            className="btn btn-primary"
                            style={{ backgroundColor: "#3182ce", borderColor: "#3182ce" }}
                        >
                            + Add Category
                        </Button>
                    </div>

                    <div className="mc-card-body">
                        {categories.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="mb-0 text-muted">No categories found</p>
                                <Button 
                                    onClick={handleAdd} 
                                    className="btn btn-primary mt-3"
                                    style={{ backgroundColor: "#3182ce", borderColor: "#3182ce" }}
                                >
                                    Create First Category
                                </Button>
                            </div>
                        ) : (
                            <div className="row">
                                {categories.map((item) => (
                                    <div className="col-xl-3 col-lg-4 col-md-6 mb-4" key={item.id}>
                                        <div className="category-card">
                                            <div className="category-card-image">
                                                <img
                                                    src={process.env.REACT_APP_IMG_URL + item.img}
                                                    alt={item.name}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/200x150?text=No+Image';
                                                    }}
                                                />
                                            </div>
                                            <div className="category-card-body">
                                                <h5 className="category-card-title">{item.name}</h5>
                                                <div className="category-card-actions">
                                                    <Button
                                                        size="sm"
                                                        variant="primary"
                                                        onClick={() => handleEdit(item)}
                                                        className="me-2"
                                                    >
                                                        <i className="material-icons">edit</i> Edit
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        onClick={() => handleDelete(item)}
                                                    >
                                                        <i className="material-icons">delete</i> Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ================= BOOTSTRAP MODAL ================= */}
                <Modal 
                    show={showModal} 
                    onHide={() => setShowModal(false)} 
                    centered
                    size="md"
                >
                    <Modal.Header closeButton className="custom-modal-header">
                        <Modal.Title className="custom-modal-title">
                            {isEdit ? "Edit Category" : "Add New Category"}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body className="custom-modal-body">
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Category Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter category name"
                                    className="custom-input"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                    autoFocus
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Category Image</Form.Label>
                                <Form.Control
                                    type="file"
                                    className="custom-input"
                                    onChange={handleFileChange}
                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                />
                                <Form.Text className="text-muted">
                                    Supported formats: JPEG, PNG, GIF, WEBP (Max 5MB)
                                </Form.Text>
                            </Form.Group>

                            {preview && (
                                <div className="text-center mt-3">
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        className="category-preview"
                                    />
                                </div>
                            )}
                        </Form>
                    </Modal.Body>

                    <Modal.Footer className="custom-modal-footer">
                        <Button 
                            variant="secondary" 
                            onClick={() => {
                                setShowModal(false);
                                setPreview(null);
                                setFormData({ id: "", name: "", image: null });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleSubmit}
                            disabled={submitting}
                            style={{ backgroundColor: "#3182ce", borderColor: "#3182ce" }}
                        >
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    {isEdit ? "Updating..." : "Saving..."}
                                </>
                            ) : (
                                isEdit ? "Update Category" : "Save Category"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>

            <style jsx>{`
                .category-card {
                    background: #fff;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .category-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 4px 15px rgba(0,0,0,0.15);
                }
                
                .category-card-image {
                    height: 150px;
                    overflow: hidden;
                }
                
                .category-card-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                
                .category-card-body {
                    padding: 15px;
                    text-align: center;
                }
                
                .category-card-title {
                    margin: 0 0 10px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                }
                
                .category-card-actions {
                    display: flex;
                    justify-content: center;
                    gap: 8px;
                }
                
                .category-card-actions button {
                    font-size: 12px;
                    padding: 4px 10px;
                }
                
                .category-card-actions button i {
                    font-size: 14px;
                    margin-right: 4px;
                    vertical-align: middle;
                }
                
                .custom-modal-header {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #dee2e6;
                    padding: 15px 20px;
                }
                
                .custom-modal-title {
                    font-weight: 600;
                    color: #333;
                }
                
                .custom-modal-body {
                    padding: 20px;
                }
                
                .custom-modal-footer {
                    border-top: 1px solid #dee2e6;
                    padding: 15px 20px;
                }
                
                .custom-input {
                    border-radius: 8px;
                    border: 1px solid #ced4da;
                    padding: 8px 12px;
                }
                
                .custom-input:focus {
                    border-color: #3182ce;
                    box-shadow: 0 0 0 0.2rem rgba(49, 130, 206, 0.25);
                }
                
                .category-preview {
                    width: 100%;
                    max-height: 200px;
                    object-fit: cover;
                    border-radius: 8px;
                    border: 1px solid #dee2e6;
                }
                
                .mc-card {
                    background: #fff;
                    border-radius: 12px;
                    margin-bottom: 20px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                
                .mc-card-header {
                    padding: 20px;
                    border-bottom: 1px solid #e9ecef;
                }
                
                .mc-card-body {
                    padding: 20px;
                }
                
                .mc-breadcrumb {
                    padding: 20px;
                }
                
                .mc-breadcrumb-title {
                    margin: 0 0 5px 0;
                    font-size: 20px;
                    font-weight: 600;
                }
                
                .mc-breadcrumb-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: flex;
                    gap: 8px;
                }
                
                .mc-breadcrumb-item {
                    color: #6c757d;
                }
                
                .mc-breadcrumb-link {
                    color: #3182ce;
                    text-decoration: none;
                }
                
                .mc-breadcrumb-item:not(:last-child):after {
                    content: "/";
                    margin-left: 8px;
                    color: #6c757d;
                }
            `}</style>
        </PageLayout>
    );
}